/**
 * ConciergeChatPanel
 *
 * Reusable transcript + input + review-panel UI for any concierge conversation
 * (see RFDs/rfd-7-ai-concierge.md). Used by three surfaces that all share the
 * same draft-then-confirm contract, differing only in which endpoint they call
 * and what happens on confirm:
 *   - the onboarding wizard's first step (ConciergeStep.tsx)
 *   - Settings ("Ask Tilly" modal)
 *   - the dashboard assistant feed ("now what?" entry point)
 */

import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { ArrowUp, Sparkles } from 'lucide-react';
import { AppButton, AppTextField, Box, CircularProgress, Typography } from '@/components';
import { ApiRequestError } from '@tilly/api-client';
import { colors, fonts, radius, spacing } from '@/theme/tokens';
import { usePreferencesStore } from '@/stores';
import { ConciergeReviewPanel, type ConciergeDraftProposal } from './ConciergeReviewPanel';
import type { OnboardingDraft } from '../types';
import type { ConciergeMessage } from '@tilly/shared';

interface DisplayMessage {
  role: 'user' | 'assistant';
  text: string;
}

/**
 * Scoped rules for rendering the model's markdown replies inside a chat
 * bubble — everything inherits color/font from the bubble so it looks right
 * on both the accent (user) and neutral (assistant) backgrounds. User-typed
 * text is never run through this; only the assistant's replies are, since
 * that's the side that actually produces markdown.
 */
function ConciergeMarkdownStyles() {
  return (
    <style>{`
      .tilly-concierge-markdown { color: inherit; }
      .tilly-concierge-markdown > *:first-child { margin-top: 0; }
      .tilly-concierge-markdown > *:last-child { margin-bottom: 0; }
      .tilly-concierge-markdown p { margin: 0 0 8px; }
      .tilly-concierge-markdown ul, .tilly-concierge-markdown ol { margin: 4px 0 8px; padding-left: 20px; }
      .tilly-concierge-markdown li { margin-bottom: 2px; }
      .tilly-concierge-markdown li > p { margin: 0; }
      .tilly-concierge-markdown strong { font-weight: 600; }
      .tilly-concierge-markdown h1, .tilly-concierge-markdown h2, .tilly-concierge-markdown h3 {
        margin: 8px 0 4px; font-size: 1em; font-weight: 600;
      }
      .tilly-concierge-markdown code {
        background: rgba(0, 0, 0, 0.08); padding: 1px 5px; border-radius: 4px;
        font-size: 0.9em; font-family: ui-monospace, monospace;
      }
      .tilly-concierge-markdown pre {
        background: rgba(0, 0, 0, 0.08); padding: ${spacing[2]}; border-radius: ${radius.md};
        overflow-x: auto; margin: 4px 0 8px;
      }
      .tilly-concierge-markdown pre code { background: none; padding: 0; }
      .tilly-concierge-markdown a { color: inherit; text-decoration: underline; }
      .tilly-concierge-markdown blockquote {
        margin: 4px 0; padding-left: ${spacing[2]}; border-left: 2px solid currentColor; opacity: 0.85;
      }
      .tilly-concierge-markdown hr { border: none; border-top: 1px solid currentColor; opacity: 0.2; margin: 8px 0; }
    `}</style>
  );
}

function ChatTurn({ role, text }: DisplayMessage) {
  const isUser = role === 'user';
  return (
    <Box style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Box
        style={{
          maxWidth: '85%',
          padding: `${spacing[2]} ${spacing[3]}`,
          borderRadius: radius.lg,
          backgroundColor: isUser ? colors.accent : colors.surfaceRaised,
          color: isUser ? colors.white : colors.textPrimary,
          fontSize: '14px',
          fontFamily: fonts.body,
          whiteSpace: isUser ? 'pre-wrap' : undefined,
          wordBreak: 'break-word',
        }}
      >
        {isUser ? (
          text
        ) : (
          <div className="tilly-concierge-markdown">
            <ReactMarkdown>{text}</ReactMarkdown>
          </div>
        )}
      </Box>
    </Box>
  );
}

/** A clarifying question the model asked this turn. When `options` is present,
 * the panel renders them as tappable buttons instead of requiring the owner
 * to type an answer the model already enumerated (e.g. "1:00 PM or 2:45 PM?"). */
export interface ConciergePendingQuestion {
  text: string;
  options?: string[];
}

export interface ConciergeTurnResponse {
  assistantMessage: string;
  draftProposal?: ConciergeDraftProposal;
  pendingQuestion?: ConciergePendingQuestion;
  messages: ConciergeMessage[];
  done: boolean;
}

export interface ConciergeChatPanelProps {
  title: string;
  description: string;
  placeholder: string;
  /** Sends the full transcript for one turn; distinguishes onboarding vs. business-scoped chat. */
  onSendTurn: (messages: ConciergeMessage[]) => Promise<ConciergeTurnResponse>;
  /**
   * Optional streaming variant of onSendTurn. When supplied, the panel renders the
   * assistant's reply incrementally instead of waiting for the whole turn. Falls back
   * to onSendTurn when absent (e.g. the mobile panel, which has no streaming variant).
   */
  onSendTurnStream?: (
    messages: ConciergeMessage[],
    handlers: { onDelta: (text: string) => void; onTool: (name: string) => void }
  ) => Promise<ConciergeTurnResponse>;
  /** Called with the accepted fields once the owner confirms the review panel. */
  onConfirmDraft: (accepted: Partial<OnboardingDraft>) => void | Promise<void>;
  /** Secondary action, always visible (e.g. "Skip", "Close"). */
  onDiscard: () => void;
  discardLabel: string;
  /** Shows a loading state on the review panel's confirm button while onConfirmDraft resolves. */
  confirming?: boolean;
  /** Shown as clickable suggestions before the owner has typed anything. */
  quickReplies?: string[];
  /** Shown when the very first turn fails (most likely ANTHROPIC_API_KEY unset). */
  unavailableMessage?: string;
  /** Label for the button shown in the unavailable state; defaults to discardLabel. */
  unavailableActionLabel?: string;
  /** Shown when the business has used its monthly concierge quota (Solo tier cap). */
  quotaExceededMessage?: string;
  /** Enables live service/provider dropdowns in the review panel's appointment
   * section. Omitted for onboarding, which never proposes an appointment. */
  businessId?: string;
}

export function ConciergeChatPanel({
  title,
  description,
  placeholder,
  onSendTurn,
  onSendTurnStream,
  onConfirmDraft,
  onDiscard,
  discardLabel,
  confirming,
  quickReplies,
  unavailableMessage,
  unavailableActionLabel,
  quotaExceededMessage,
  businessId,
}: ConciergeChatPanelProps) {
  const { t } = useTranslation(['admin', 'common']);
  const resolvedUnavailableMessage =
    unavailableMessage ?? t('admin:setupWizard.conciergeUnavailable');
  const resolvedQuotaExceededMessage =
    quotaExceededMessage ?? t('admin:setupWizard.conciergeQuotaExceeded');
  const reduceMotion = usePreferencesStore((state) => state.reduceMotion);
  const [transcript, setTranscript] = useState<ConciergeMessage[]>([]);
  const [displayLog, setDisplayLog] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [draftProposal, setDraftProposal] = useState<ConciergeDraftProposal | null>(null);
  // The model's latest clarifying question with tappable options, if it offered
  // any this turn. Cleared as soon as a new turn starts (see sendMessage) so a
  // stale question's buttons don't linger once the owner has moved on.
  const [pendingQuestion, setPendingQuestion] = useState<ConciergePendingQuestion | null>(null);
  // Live partial text for the turn currently streaming in. Cleared whenever a tool
  // call starts (the preamble text before it is superseded, not worth keeping) and
  // on turn completion, once its final text has been committed to displayLog.
  const [streamingText, setStreamingText] = useState('');
  // Set on the very first turn's failure — most likely means ANTHROPIC_API_KEY is
  // unset (RFD-7 guardrail: product works fully with the concierge off). A failure
  // mid-conversation is shown inline instead, since the owner may just want to retry.
  const [unavailable, setUnavailable] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const turnMutation = useMutation({
    mutationFn: (messages: ConciergeMessage[]) => {
      if (!onSendTurnStream) return onSendTurn(messages);
      setStreamingText('');
      return onSendTurnStream(messages, {
        onDelta: (text) => setStreamingText((prev) => prev + text),
        onTool: () => setStreamingText(''),
      });
    },
    onSuccess: (response) => {
      setTranscript(response.messages);
      setStreamingText('');
      if (response.assistantMessage) {
        setDisplayLog((prev) => [...prev, { role: 'assistant', text: response.assistantMessage }]);
      }
      if (response.draftProposal) {
        setDraftProposal(response.draftProposal);
      }
      setPendingQuestion(response.pendingQuestion ?? null);
    },
    onError: (error) => {
      setStreamingText('');
      if (error instanceof ApiRequestError && error.code === 'PLAN_LIMIT_EXCEEDED') {
        setQuotaExceeded(true);
        return;
      }
      if (displayLog.length <= 1) {
        setUnavailable(true);
      }
    },
  });

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || turnMutation.isPending) return;

      const nextTranscript: ConciergeMessage[] = [
        ...transcript,
        { role: 'user', content: trimmed },
      ];
      setTranscript(nextTranscript);
      setDisplayLog((prev) => [...prev, { role: 'user', text: trimmed }]);
      setInput('');
      setPendingQuestion(null);
      turnMutation.mutate(nextTranscript);
    },
    [transcript, turnMutation]
  );

  const handleSend = useCallback(() => sendMessage(input), [input, sendMessage]);

  const handleConfirmDraft = useCallback(
    (accepted: Partial<OnboardingDraft>) => {
      void onConfirmDraft(accepted);
    },
    [onConfirmDraft]
  );

  if (unavailable) {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        <Typography style={{ color: colors.textMuted }}>{resolvedUnavailableMessage}</Typography>
        <AppButton onClick={onDiscard}>{unavailableActionLabel ?? discardLabel}</AppButton>
      </Box>
    );
  }

  if (quotaExceeded) {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        <Typography style={{ color: colors.textMuted }}>{resolvedQuotaExceededMessage}</Typography>
        <AppButton onClick={onDiscard}>{discardLabel}</AppButton>
      </Box>
    );
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
      <ConciergeMarkdownStyles />
      <Box>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing[2],
            marginBottom: spacing[1],
          }}
        >
          <Sparkles size={20} color={colors.accent} />
          <Typography
            style={{
              fontFamily: fonts.heading,
              fontSize: '20px',
              fontWeight: 600,
              color: colors.textPrimary,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography style={{ color: colors.textMuted, fontSize: '14px' }}>{description}</Typography>
      </Box>

      {displayLog.length > 0 && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
            maxHeight: '320px',
            overflowY: 'auto',
            padding: spacing[1],
          }}
        >
          {displayLog.map((message, index) => (
            <ChatTurn key={index} role={message.role} text={message.text} />
          ))}
          {turnMutation.isPending && streamingText && (
            <ChatTurn role="assistant" text={streamingText} />
          )}
          {turnMutation.isPending && !streamingText && (
            <Box style={{ display: 'flex', justifyContent: 'flex-start', padding: spacing[2] }}>
              <CircularProgress
                size={16}
                style={reduceMotion ? { animationDuration: '2s' } : undefined}
              />
            </Box>
          )}
          {turnMutation.isError && !unavailable && (
            <Typography style={{ color: colors.textMuted, fontSize: '13px' }}>
              {t('admin:setupWizard.conciergeTurnFailed')}
            </Typography>
          )}
        </Box>
      )}

      {draftProposal ? (
        <ConciergeReviewPanel
          proposal={draftProposal}
          onConfirm={handleConfirmDraft}
          onDiscard={onDiscard}
          discardLabel={discardLabel}
          confirming={confirming}
          businessId={businessId}
        />
      ) : (
        <>
          {quickReplies && quickReplies.length > 0 && displayLog.length === 0 && (
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: spacing[2] }}>
              {quickReplies.map((reply) => (
                <AppButton
                  key={reply}
                  variant="secondary"
                  size="sm"
                  onClick={() => sendMessage(reply)}
                >
                  {reply}
                </AppButton>
              ))}
            </Box>
          )}
          {pendingQuestion?.options && pendingQuestion.options.length > 0 && (
            <Box
              data-testid="concierge-question-options"
              style={{ display: 'flex', flexWrap: 'wrap', gap: spacing[2] }}
            >
              {pendingQuestion.options.map((option) => (
                <AppButton
                  key={option}
                  variant="secondary"
                  size="sm"
                  disabled={turnMutation.isPending}
                  onClick={() => sendMessage(option)}
                >
                  {option}
                </AppButton>
              ))}
            </Box>
          )}
          <Box style={{ display: 'flex', gap: spacing[2], alignItems: 'flex-end' }}>
            <AppTextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder={placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={turnMutation.isPending}
              autoFocus
            />
            <AppButton
              onClick={handleSend}
              disabled={!input.trim() || turnMutation.isPending}
              aria-label={t('common:send' as never) as unknown as string}
            >
              <ArrowUp size={18} />
            </AppButton>
          </Box>
        </>
      )}

      <Box style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <AppButton variant="ghost" onClick={onDiscard}>
          {discardLabel}
        </AppButton>
      </Box>
    </Box>
  );
}
